// Bridge to the FUN88 native lite-app wrappers (Android/iOS). The wrappers inject
// `window.Fun88Native`; on plain web these helpers are inert no-ops.

// Logical event name -> Adjust event token (FUN88 lite Adjust app `azbm4m5w2o00`).
const NATIVE_EVENT_TOKENS: Record<string, string> = {
  af_login: 'npryfz',
  af_open_game: 'hy62mh',
  af_registration: 'cam5w0',
}

/** True when running inside a native wrapper (FUN88 lite app or legacy RN webview). */
export const isInNativeApp = () =>
  typeof window !== 'undefined' &&
  Boolean(window.Fun88Native || window.ReactNativeWebView)

/**
 * True when the native wrapper advertises that it can complete web OAuth (opens the sign-in in a
 * system browser and returns to the app). New FUN88 lite-app builds set `Fun88Native.supportsOAuth`
 * in their bridge shim; older installs and the legacy RN webview don't, so this stays false there.
 */
export const nativeSupportsOAuth = () =>
  typeof window !== 'undefined' && Boolean(window.Fun88Native?.supportsOAuth)

/**
 * Whether to show the social-login buttons. They rely on OAuth in a system browser, so show them
 * on plain web always, but inside a native wrapper only when the build advertises OAuth support —
 * keeping them hidden on older app installs (and the legacy RN webview) where they can't complete.
 */
export const shouldShowSocialAuth = () =>
  !isInNativeApp() || nativeSupportsOAuth()

/** Fire an Adjust event through the native SDK. No-op on plain web / unknown events. */
export const trackNativeEvent = (
  name: string,
  params: Record<string, unknown> = {},
) => {
  const bridge = typeof window !== 'undefined' ? window.Fun88Native : undefined
  const token = NATIVE_EVENT_TOKENS[name]
  if (!bridge || typeof bridge.trackEvent !== 'function' || !token) return
  try {
    bridge.trackEvent(token, params)
  } catch {
    // ignore bridge failures
  }
}

/**
 * Pull the native Adjust attribution meta (`adjust` + `app_package_info`) to attach to
 * register / submitPay. Resolves null on plain web or if the bridge doesn't answer in time.
 */
export const getNativeAttributionMeta = (): Promise<Record<
  string,
  unknown
> | null> => {
  const getMeta =
    typeof window !== 'undefined' ? window.Fun88Native?.getAttributionMeta : undefined
  if (!getMeta) return Promise.resolve(null)
  const fromBridge = Promise.resolve()
    .then(() => getMeta())
    .catch(() => null)
  const timeout = new Promise<null>(resolve =>
    setTimeout(() => resolve(null), 1500),
  )
  return Promise.race([fromBridge, timeout])
}

/**
 * Pull the device's FCM registration token from the native wrapper, to register it against the
 * logged-in member (see utils/fcmToken). Resolves null on plain web, if the bridge lacks
 * `getFcmToken`, or if it doesn't answer in time.
 */
export const getNativeFcmToken = (): Promise<string | null> => {
  const getToken =
    typeof window !== 'undefined' ? window.Fun88Native?.getFcmToken : undefined
  if (!getToken) return Promise.resolve(null)
  const fromBridge = Promise.resolve()
    .then(() => getToken())
    .catch(() => null)
  const timeout = new Promise<null>(resolve =>
    setTimeout(() => resolve(null), 1500),
  )
  return Promise.race([fromBridge, timeout])
}

/**
 * True when the native wrapper can save an image to the gallery. Inside a WebView an
 * `<a download>` on a `data:` URL is a no-op, so callers should hand off to the bridge when this
 * is true and only fall back to the anchor trick on plain web.
 */
export const canNativeSaveImage = () =>
  typeof window !== 'undefined' &&
  typeof window.Fun88Native?.saveImage === 'function'

/**
 * True when the native wrapper can open the OS share sheet for an image. Android WebView has no
 * `navigator.share`, so the share button relies on this to show/work inside the app.
 */
export const canNativeShareImage = () =>
  typeof window !== 'undefined' &&
  typeof window.Fun88Native?.shareImage === 'function'

/**
 * Race a native-bridge call against a timeout so a dropped native callback can't leave the poster
 * action stuck (same recovery pattern as getNativeAttributionMeta / getNativeFcmToken above).
 * In normal use both callbacks resolve well within the timeout; it only fires if the bridge drops.
 */
const withTimeout = (call: Promise<boolean>, ms: number): Promise<boolean> => {
  const timeout = new Promise<boolean>(resolve => setTimeout(() => resolve(false), ms))
  return Promise.race([call, timeout])
}

/**
 * Save a data-URL / base64 image to the device gallery through the native wrapper. Resolves false
 * on plain web, if the bridge lacks `saveImage`, if the native save fails/denies permission, or if
 * the native callback is dropped (20s timeout).
 */
export const nativeSaveImage = (
  dataUrl: string,
  filename: string,
): Promise<boolean> => {
  const save =
    typeof window !== 'undefined' ? window.Fun88Native?.saveImage : undefined
  if (!save) return Promise.resolve(false)
  const call = Promise.resolve()
    .then(() => save(dataUrl, filename))
    .then(res => Boolean(res?.success))
    .catch(() => false)
  return withTimeout(call, 20_000)
}

/**
 * Share a data-URL / base64 image via the OS share sheet through the native wrapper. Resolves
 * false on plain web, if the bridge lacks `shareImage`, if the user cancels / it fails, or if the
 * native callback is dropped (120s timeout — long because the share sheet is user-interactive).
 */
export const nativeShareImage = (
  dataUrl: string,
  filename: string,
): Promise<boolean> => {
  const share =
    typeof window !== 'undefined' ? window.Fun88Native?.shareImage : undefined
  if (!share) return Promise.resolve(false)
  const call = Promise.resolve()
    .then(() => share(dataUrl, filename))
    .then(res => Boolean(res?.success))
    .catch(() => false)
  return withTimeout(call, 120_000)
}
